// ไฟล์: frontend/src/pages/teacher/Portfolios.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import api from '../../services/api';
import Swal from 'sweetalert2';
import { DEFAULT_AVATAR } from '../../assets/default_avatar';

function TeacherPortfolios() {
  const [portfolios, setPortfolios] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [formData, setFormData] = useState({
    student_id: '',
    title: '',
    description: '',
    semester: '',
    category: '',
    achievement_level: 'fair',
    files: []
  });
  
  useEffect(() => {
    fetchMyClasses();
    fetchMyPortfolios();
  }, []);
  
  useEffect(() => {
    if (selectedClassId) {
      fetchStudentsInClass(selectedClassId);
    }
  }, [selectedClassId]);
  
  const fetchMyClasses = async () => {
    try {
      const response = await api.class.getMyClasses();
      setClasses(response.data.data || []);
      
      // เลือกชั้นเรียนแรกเป็นค่าเริ่มต้น
      if (response.data.data && response.data.data.length > 0) {
        setSelectedClassId(response.data.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };
  
  const fetchStudentsInClass = async (classId) => {
    try {
      const response = await api.student.getStudentsByClass(classId);
      setStudents(response.data.data || []);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };
  
  const fetchMyPortfolios = async () => {
    try {
      setIsLoading(true);
      const response = await api.portfolio.getMyPortfolios();
      setPortfolios(response.data.data || []);
    } catch (error) {
      console.error('Error fetching portfolios:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลแฟ้มผลงานได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchPortfolioDetail = async (id) => {
    try {
      const response = await api.portfolio.getPortfolioById(id);
      setSelectedPortfolio(response.data.data);
      setViewMode(true);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching portfolio detail:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลแฟ้มผลงานได้',
        confirmButtonColor: '#ff6b01'
      });
    }
  };
  
  const handleShowAddModal = () => {
    setFormData({
      student_id: students.length > 0 ? students[0].id : '',
      title: '',
      description: '',
      semester: `${new Date().getFullYear()}/1`, // ค่าเริ่มต้น
      category: '',
      achievement_level: 'fair',
      files: []
    });
    setViewMode(false);
    setShowModal(true);
  };
  
  const handleShowEditModal = async (portfolio) => {
    try {
      const response = await api.portfolio.getPortfolioById(portfolio.id);
      const portfolioDetail = response.data.data;
      
      setFormData({
        student_id: portfolioDetail.student_id,
        title: portfolioDetail.title,
        description: portfolioDetail.description || '',
        semester: portfolioDetail.semester || '',
        category: portfolioDetail.category || '',
        achievement_level: portfolioDetail.achievement_level || 'fair',
        files: []
      });
      
      setSelectedPortfolio(portfolioDetail);
      setViewMode(false);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching portfolio for edit:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลแฟ้มผลงานได้',
        confirmButtonColor: '#ff6b01'
      });
    }
  };
  
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPortfolio(null);
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData({
      ...formData,
      files: files
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // ตรวจสอบข้อมูลที่จำเป็น
    if (!formData.student_id || !formData.title) {
      Swal.fire({
        icon: 'warning',
        title: 'กรุณากรอกข้อมูลให้ครบถ้วน',
        text: 'กรุณาเลือกนักเรียนและระบุชื่อผลงาน',
        confirmButtonColor: '#ff6b01'
      });
      return;
    }
    
    try {
      setIsLoading(true);
      
      // สร้าง FormData สำหรับส่งข้อมูลและไฟล์
      const data = new FormData();
      data.append('student_id', formData.student_id);
      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('semester', formData.semester);
      data.append('category', formData.category);
      data.append('achievement_level', formData.achievement_level);
      
      // เพิ่มไฟล์ (ถ้ามี)
      formData.files.forEach(file => {
        data.append('files', file);
      });
      
      let response;
      if (selectedPortfolio) {
        // อัปเดตแฟ้มผลงาน
        response = await api.portfolio.updatePortfolio(selectedPortfolio.id, data);
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: 'อัปเดตแฟ้มผลงานเรียบร้อยแล้ว',
          confirmButtonColor: '#ff6b01'
        });
      } else {
        // เพิ่มแฟ้มผลงานใหม่
        response = await api.portfolio.createPortfolio(data);
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: 'เพิ่มแฟ้มผลงานเรียบร้อยแล้ว',
          confirmButtonColor: '#ff6b01'
        });
      }
      
      // ปิด Modal และโหลดข้อมูลใหม่
      setShowModal(false);
      fetchMyPortfolios();
    } catch (error) {
      console.error('Error saving portfolio:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถบันทึกแฟ้มผลงานได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDelete = (id) => {
    Swal.fire({
      title: 'ยืนยันการลบ',
      text: 'คุณแน่ใจหรือไม่ว่าต้องการลบแฟ้มผลงานนี้?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ff6b01',
      cancelButtonColor: '#d33',
      confirmButtonText: 'ใช่, ลบข้อมูล',
      cancelButtonText: 'ยกเลิก'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.portfolio.deletePortfolio(id);
          Swal.fire({
            icon: 'success',
            title: 'ลบข้อมูลสำเร็จ',
            text: 'ลบแฟ้มผลงานเรียบร้อยแล้ว',
            confirmButtonColor: '#ff6b01'
          });
          fetchMyPortfolios();
        } catch (error) {
          console.error('Error deleting portfolio:', error);
          Swal.fire({
            icon: 'error',
            title: 'เกิดข้อผิดพลาด',
            text: 'ไม่สามารถลบแฟ้มผลงานได้',
            confirmButtonColor: '#ff6b01'
          });
        }
      }
    });
  };
  
  const handleDeleteFile = async (fileId) => {
    Swal.fire({
      title: 'ยืนยันการลบ',
      text: 'คุณแน่ใจหรือไม่ว่าต้องการลบไฟล์แนบนี้?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ff6b01',
      cancelButtonColor: '#d33',
      confirmButtonText: 'ใช่, ลบไฟล์',
      cancelButtonText: 'ยกเลิก'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.portfolio.deletePortfolioFile(fileId);
          
          // อัปเดตข้อมูลแฟ้มผลงานในหน้าจอ
          if (selectedPortfolio && selectedPortfolio.files) {
            setSelectedPortfolio({
              ...selectedPortfolio,
              files: selectedPortfolio.files.filter(file => file.id !== fileId)
            });
          }
          
          Swal.fire({
            icon: 'success',
            title: 'ลบไฟล์สำเร็จ',
            text: 'ลบไฟล์แนบเรียบร้อยแล้ว',
            confirmButtonColor: '#ff6b01'
          });
        } catch (error) {
          console.error('Error deleting file:', error);
          Swal.fire({
            icon: 'error',
            title: 'เกิดข้อผิดพลาด',
            text: 'ไม่สามารถลบไฟล์แนบได้',
            confirmButtonColor: '#ff6b01'
          });
        }
      }
    });
  };
  
  // แสดงป้ายระดับผลงาน
  const renderAchievementBadge = (level) => {
    let badgeClass = '';
    let text = '';
    
    switch (level) {
      case 'excellent':
        badgeClass = 'bg-success';
        text = 'ดีเยี่ยม';
        break;
      case 'good':
        badgeClass = 'bg-primary';
        text = 'ดี';
        break;
      case 'fair':
        badgeClass = 'bg-warning';
        text = 'พอใช้';
        break;
      case 'improve':
        badgeClass = 'bg-danger';
        text = 'ต้องปรับปรุง';
        break;
      default:
        badgeClass = 'bg-secondary';
        text = 'ไม่ระบุ';
    }
    
    return <span className={`badge ${badgeClass}`}>{text}</span>;
  };
  
  // แสดงไอคอนประเภทไฟล์
  const getFileIcon = (fileType) => {
    if (fileType.includes('image')) {
      return <i className="fas fa-file-image text-info"></i>;
    } else if (fileType.includes('pdf')) {
      return <i className="fas fa-file-pdf text-danger"></i>;
    } else if (fileType.includes('word') || fileType.includes('document')) {
      return <i className="fas fa-file-word text-primary"></i>;
    } else if (fileType.includes('excel') || fileType.includes('sheet')) {
      return <i className="fas fa-file-excel text-success"></i>;
    } else if (fileType.includes('powerpoint') || fileType.includes('presentation')) {
      return <i className="fas fa-file-powerpoint text-warning"></i>;
    } else {
      return <i className="fas fa-file text-secondary"></i>;
    }
  };
  
  return (
    <div className="wrapper">
      <Header />
      <Sidebar />
      
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">แฟ้มผลงานนักเรียน</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <a href="/teacher/dashboard">หน้าหลัก</a>
                  </li>
                  <li className="breadcrumb-item active">แฟ้มผลงาน</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        
        <section className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-md-12">
                <div className="card card-orange">
                  <div className="card-header">
                    <h3 className="card-title">เลือกชั้นเรียน</h3>
                  </div>
                  <div className="card-body">
                    {classes.length === 0 ? (
                      <div className="alert alert-warning">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        คุณยังไม่ได้รับมอบหมายให้ดูแลชั้นเรียนใดๆ กรุณาติดต่อผู้ดูแลระบบ
                      </div>
                    ) : (
                      <div className="form-group">
                        <select
                          className="form-control"
                          value={selectedClassId}
                          onChange={(e) => setSelectedClassId(e.target.value)}
                        >
                          {classes.map(classItem => (
                            <option key={classItem.id} value={classItem.id}>
                              {classItem.class_name} (ห้อง {classItem.room_number})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="card card-orange">
              <div className="card-header">
                <h3 className="card-title">แฟ้มผลงานนักเรียน</h3>
                <div className="card-tools">
                  <button
                    type="button"
                    className="btn btn-tool"
                    onClick={handleShowAddModal}
                    disabled={students.length === 0}
                  >
                    <i className="fas fa-plus"></i> เพิ่มแฟ้มผลงาน
                  </button>
                </div>
              </div>
              <div className="card-body">
                {isLoading ? (
                  <div className="loading-spinner">
                    <i className="fas fa-spinner fa-spin"></i>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-bordered table-striped">
                      <thead>
                        <tr>
                          <th style={{ width: '60px' }}>ลำดับ</th>
                          <th style={{ width: '80px' }}>รูปภาพ</th>
                          <th>ชื่อนักเรียน</th>
                          <th>ชื่อผลงาน</th>
                          <th style={{ width: '100px' }}>ภาคเรียน</th>
                          <th style={{ width: '120px' }}>หมวดหมู่</th>
                          <th style={{ width: '100px' }}>ระดับผลงาน</th>
                          <th style={{ width: '150px' }}>ตัวเลือก</th>
                        </tr>
                      </thead>
                      <tbody>
                        {portfolios.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center">
                              ไม่พบข้อมูลแฟ้มผลงาน
                            </td>
                          </tr>
                        ) : (
                          portfolios.map((portfolio, index) => (
                            <tr key={portfolio.id}>
                              <td>{index + 1}</td>
                              <td>
                                <img
                                  src={`https://cms2568.backend.kruwirat.com/uploads/${portfolio.student_pic}`}
                                  alt={`${portfolio.student_first_name} ${portfolio.student_last_name}`}
                                  className="student-profile-pic-sm"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = DEFAULT_AVATAR;
                                  }}
                                />
                              </td>
                              <td>
                                {portfolio.student_first_name} {portfolio.student_last_name}
                                <small className="d-block text-muted">
                                  รหัส: {portfolio.student_code}
                                </small>
                              </td>
                              <td>{portfolio.title}</td>
                              <td>{portfolio.semester || '-'}</td>
                              <td>{portfolio.category || '-'}</td>
                              <td>
                                {renderAchievementBadge(portfolio.achievement_level)}
                              </td>
                              <td>
                                <button
                                  className="btn btn-info btn-sm mr-1"
                                  onClick={() => fetchPortfolioDetail(portfolio.id)}
                                >
                                  <i className="fas fa-eye"></i>
                                </button>
                                <button
                                  className="btn btn-warning btn-sm mr-1"
                                  onClick={() => handleShowEditModal(portfolio)}
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => handleDelete(portfolio.id)}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
      
      {/* Modal เพิ่ม/แก้ไข/ดูแฟ้มผลงาน */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block', paddingRight: '15px' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-orange">
                <h5 className="modal-title">
                  {viewMode ? 'ดูแฟ้มผลงาน' : 
                   selectedPortfolio ? 'แก้ไขแฟ้มผลงาน' : 'เพิ่มแฟ้มผลงานใหม่'}
                </h5>
                <button type="button" className="close" onClick={handleCloseModal}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              
              {viewMode ? (
                <div className="modal-body">
                  {selectedPortfolio && (
                    <div>
                      <div className="row mb-3">
                        <div className="col-md-3 text-center">
                          <img
                            src={`https://cms2568.backend.kruwirat.com/uploads/${selectedPortfolio.student_pic}`}
                            alt={`${selectedPortfolio.student_first_name} ${selectedPortfolio.student_last_name}`}
                            className="student-profile-pic"
                            style={{ width: '120px', height: '120px' }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = DEFAULT_AVATAR;
                            }}
                          />
                        </div>
                        <div className="col-md-9">
                          <h4>{selectedPortfolio.title}</h4>
                          <p>
                            <strong>นักเรียน:</strong> {selectedPortfolio.student_first_name} {selectedPortfolio.student_last_name} 
                            (รหัส: {selectedPortfolio.student_code})
                          </p>
                          <p>
                            <strong>ภาคเรียน:</strong> {selectedPortfolio.semester || '-'} &nbsp;
                            <strong>หมวดหมู่:</strong> {selectedPortfolio.category || '-'} &nbsp;
                            <strong>ระดับผลงาน:</strong> {renderAchievementBadge(selectedPortfolio.achievement_level)}
                          </p>
                          <p>
                            <strong>บันทึกโดย:</strong> {selectedPortfolio.teacher_first_name} {selectedPortfolio.teacher_last_name} &nbsp;
                            <strong>วันที่:</strong> {new Date(selectedPortfolio.created_at).toLocaleDateString('th-TH')}
                          </p>
                        </div>
                      </div>
                      
                      {selectedPortfolio.description && (
                        <div className="row mb-3">
                          <div className="col-12">
                            <div className="card">
                              <div className="card-header">
                                <h3 className="card-title">รายละเอียด</h3>
                              </div>
                              <div className="card-body">
                                <p style={{ whiteSpace: 'pre-wrap' }}>{selectedPortfolio.description}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {selectedPortfolio.files && selectedPortfolio.files.length > 0 && (
                        <div className="row">
                          <div className="col-12">
                            <div className="card">
                              <div className="card-header">
                                <h3 className="card-title">ไฟล์แนบ</h3>
                              </div>
                              <div className="card-body">
                                <ul className="list-group">
                                  {selectedPortfolio.files.map(file => (
                                    <li key={file.id} className="list-group-item d-flex justify-content-between align-items-center">
                                      <div>
                                        {getFileIcon(file.file_type)} {file.file_name}
                                        <small className="text-muted ml-2">
                                          ({Math.round(file.file_size / 1024)} KB)
                                        </small>
                                      </div>
                                      <div>
                                        <a 
                                          href={`https://cms2568.backend.kruwirat.com/${file.file_path}`} 
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="btn btn-sm btn-primary mr-1"
                                        >
                                          <i className="fas fa-download"></i>
                                        </a>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-danger"
                                          onClick={() => handleDeleteFile(file.id)}
                                        >
                                          <i className="fas fa-trash"></i>
                                        </button>
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="form-group">
                      <label>นักเรียน <span className="text-danger">*</span></label>
                      <select
                        className="form-control"
                        name="student_id"
                        value={formData.student_id}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">-- เลือกนักเรียน --</option>
                        {students.map(student => (
                          <option key={student.id} value={student.id}>
                            {student.first_name} {student.last_name} (รหัส: {student.student_id})
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label>ชื่อผลงาน <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="row">
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>ภาคเรียน</label>
                          <input
                            type="text"
                            className="form-control"
                            name="semester"
                            value={formData.semester}
                            onChange={handleInputChange}
                            placeholder="เช่น 1/2565"
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>หมวดหมู่</label>
                          <input
                            type="text"
                            className="form-control"
                            name="category"
                            value={formData.category}
                            onChange={handleInputChange}
                            placeholder="เช่น วิทยาศาสตร์, ศิลปะ"
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label>ระดับผลงาน</label>
                          <select
                            className="form-control"
                            name="achievement_level"
                            value={formData.achievement_level}
                            onChange={handleInputChange}
                          >
                            <option value="excellent">ดีเยี่ยม</option>
                            <option value="good">ดี</option>
                            <option value="fair">พอใช้</option>
                            <option value="improve">ต้องปรับปรุง</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label>รายละเอียด</label>
                      <textarea
                        className="form-control"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows="4"
                      ></textarea>
                    </div>
                    
                    <div className="form-group">
                      <label>ไฟล์แนบ (สูงสุด 5 ไฟล์)</label>
                      <div className="custom-file">
                        <input
                          type="file"
                          className="custom-file-input"
                          id="files"
                          name="files"
                          onChange={handleFileChange}
                          multiple
                          accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                        />
                        <label className="custom-file-label" htmlFor="files">
                          {formData.files.length > 0 
                            ? `${formData.files.length} ไฟล์ที่เลือก` 
                            : 'เลือกไฟล์'}
                        </label>
                      </div>
                      <small className="form-text text-muted">
                        รองรับไฟล์รูปภาพ, PDF, Word, Excel, PowerPoint และข้อความ (สูงสุด 10MB ต่อไฟล์)
                      </small>
                      
                      {formData.files.length > 0 && (
                        <ul className="list-group mt-2">
                          {formData.files.map((file, index) => (
                            <li key={index} className="list-group-item py-2">
                              {file.type.includes('image') ? <i className="fas fa-file-image text-info mr-2"></i> :
                               file.type.includes('pdf') ? <i className="fas fa-file-pdf text-danger mr-2"></i> :
                               file.type.includes('word') ? <i className="fas fa-file-word text-primary mr-2"></i> :
                               file.type.includes('sheet') ? <i className="fas fa-file-excel text-success mr-2"></i> :
                               file.type.includes('presentation') ? <i className="fas fa-file-powerpoint text-warning mr-2"></i> :
                               <i className="fas fa-file text-secondary mr-2"></i>}
                              {file.name} ({Math.round(file.size / 1024)} KB)
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    
                    {selectedPortfolio && selectedPortfolio.files && selectedPortfolio.files.length > 0 && (
                      <div className="form-group">
                        <label>ไฟล์แนบที่มีอยู่</label>
                        <ul className="list-group">
                          {selectedPortfolio.files.map(file => (
                            <li key={file.id} className="list-group-item d-flex justify-content-between align-items-center py-2">
                              <div>
                                {getFileIcon(file.file_type)} {file.file_name}
                                <small className="text-muted ml-2">
                                  ({Math.round(file.file_size / 1024)} KB)
                                </small>
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDeleteFile(file.id)}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                      ยกเลิก
                    </button>
                    <button type="submit" className="btn btn-orange" disabled={isLoading}>
                      {isLoading ? (
                        <><i className="fas fa-spinner fa-spin"></i> กำลังบันทึก...</>
                      ) : (
                        <>บันทึก</>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
          
          {/* พื้นหลังทึบ */}
          <div className="modal- fade show"></div>
        </div>
      )}
      
      <Footer />
    </div>
  );
}

export default TeacherPortfolios;